/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener("DOMContentLoaded", function () {
  const contentSections = document.querySelectorAll(
    ".customer-support__section"
  );
  const navigation = document.querySelector(".customer-support__navigation");
  const navigationLinks = document.querySelectorAll(
    ".customer-support__navigation a"
  );
  const collectionFacet = document.querySelector(".product-facet");

  function smoothScroll(target) {
    let targetOffsetTop = target.getBoundingClientRect().top + window.scrollY;
    let startPosition = window.scrollY;
    let distance = targetOffsetTop - startPosition;
    let duration = 800;
    let startTime = null;

    function animationStep(timestamp) {
      if (!startTime) startTime = timestamp;
      let progress = timestamp - startTime;
      let percentage = Math.min(progress / duration, 1);
      window.scrollTo(0, startPosition + distance * percentage);

      if (progress < duration) {
        requestAnimationFrame(animationStep);
      }
    }

    requestAnimationFrame(animationStep);
  }

  function updateNavigation() {
    let windowHeight = window.innerHeight;

    contentSections.forEach(function (section) {
      let sectionName = section.getAttribute("id");
      let navigationMatch = navigation.querySelector(
        'a[href="#' + sectionName + '"]'
      );
      let sectionOffsetTop = section.getBoundingClientRect().top;

      if (
        sectionOffsetTop - windowHeight / 2 < 0 &&
        sectionOffsetTop + section.offsetHeight - windowHeight / 2 > 0
      ) {
        navigationMatch.classList.add("active-section");
      } else {
        navigationMatch.classList.remove("active-section");
      }

      navigationLinks.forEach(function (link) {
        let targetId = link.getAttribute("href").slice(1); // Remove the leading '#'
        let targetSection = document.getElementById(targetId);

        if (!targetSection) {
          link.classList.add("brand-no-content");
        } else {
          link.classList.remove("brand-no-content");
        }
      });
    });
  }

  if (navigation) {
    navigation.addEventListener("click", function (event) {
      event.preventDefault();
      let target = event.target;
      if (target.tagName === "A") {
        let hash = target.getAttribute("href");
        let targetSection = document.querySelector(hash);
        if (targetSection) {
          smoothScroll(targetSection);
        }
      }
    });
  }
  function showButtons() {
    // EQ-FILTERFIX-2026-08-22: "Meer bekijken" hing aan listeners die per knop
    // werden gekoppeld. Het filterpaneel wordt bij elke filteractie opnieuw
    // opgebouwd (facet-filters.liquid zet #facet-filters.innerHTML), waardoor
    // die listeners verdwenen: na je eerste filter deed Designer/Categorie
    // niets meer. Een gedelegeerde listener op document overleeft elke herbouw.
    // Het Beschikbaarheid-blok verbergen we niet meer hier maar met CSS
    // (.product-facet__filter-item--auto-hidden), om dezelfde reden.
    if (showButtons._bound) return;
    showButtons._bound = true;

    var VISIBLE_COUNT = 6; // facet-filters.liquid toont er standaard 6

    document.addEventListener("click", function (event) {
      var trigger =
        event.target &&
        event.target.closest &&
        event.target.closest(".show-all-button, .show-all-button-swatch");
      if (!trigger) return;

      event.preventDefault();

      var isSwatch = trigger.classList.contains("show-all-button-swatch");
      var container = isSwatch
        ? trigger.closest(".block-swatch-list")
        : trigger.closest(".product-facet__filter-item");
      if (!container) return;

      var itemSelector = isSwatch ? ".block-swatch" : ".checkbox-container";
      var moreButton = container.querySelector(
        isSwatch ? ".show-all-button-swatch.show_more" : ".show-all-button.show_more"
      );
      var lessButton = container.querySelector(
        isSwatch ? ".show-all-button-swatch.show_less" : ".show-all-button.show_less"
      );
      var expand = trigger.classList.contains("show_more");

      Array.prototype.forEach.call(
        container.querySelectorAll(itemSelector),
        function (item, index) {
          if (expand) {
            item.classList.remove("hidden");
          } else if (index >= VISIBLE_COUNT) {
            item.classList.add("hidden");
          }
        }
      );

      if (moreButton) moreButton.style.display = expand ? "none" : "block";
      if (lessButton) lessButton.style.display = expand ? "block" : "none";
    });
  }

  window.addEventListener("scroll", function () {
    updateNavigation();
  });

  updateNavigation();
  showButtons();

  // sada

  (function () {
    var rs = history.replaceState;
    history.replaceState = function () {
      rs.apply(history, arguments); // preserve normal functionality
      setTimeout(function () {
        showButtons();
      }, 1500);
    };
  })();

  //search page

  const mainResult = document.getElementById("main-result");
  if (mainResult) {
    const imageOverlay = document.querySelector(
      ".shopify-section--image-with-text-overlay"
    );
    imageOverlay.style.display = "none";
  }
});

class ProductItemColor extends HTMLElement {
  constructor() {
    super();

    this.id = this.getAttribute("data-id");
    this.getColors();
  }

  getColors() {
    const imageVariant = document.getElementById(
      "variant-image-tile-" + this.id
    );
    const containerID = document.getElementById("product-info-item-" + this.id);
    if (imageVariant) {
      const productType = imageVariant.getAttribute("data-product-type");
      const productVendor = imageVariant.getAttribute("data-product-vendor");
      const productID = imageVariant.getAttribute("data-product-id");
      const originalString = imageVariant.getAttribute("data-product-meta");
      const lastIndex = originalString.lastIndexOf("-");
      const metafieldID = originalString.substring(0, lastIndex);

      // const metafieldID = imageVariant.getAttribute("data-product-meta");
      const colorOption = document.getElementById(
        "product-variant-image-option"
      );

      const query = `query {
    products(first: 250, query: "vendor:${productVendor}") {
      edges {
        node {   
          id
          metafield(key: "article_code_supplier", namespace: "specs") {
            value
          }
        }
      }
    }
  }`;

      const requestOptions = {
        method: "POST",
        headers,
        body: query,
      };

      return fetch(
        `https://${Shopify.shop}/api/2023-07/graphql.json`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        })
        .then(({ data }) => {
          const formattedData = data.products.edges
            .map((productNode) => ({
              objectType: "product",
              id: parseInt(
                productNode.node.id.replace("gid://shopify/Product/", "")
              ),
              metafield: productNode.node.metafield?.value,
            }))
            .filter((product) => {
              return product.metafield
                .toLowerCase()
                .includes(metafieldID.toLowerCase());
            });

          if (formattedData.length) {
            const variantImageDiv = document.getElementById(
              "color-count-variant-" + this.id
            );

            const imageContents = `
        <span>${formattedData.length}
        </span>
`;

            const tempContainer = document.createElement("div");

            tempContainer.innerHTML = imageContents;

            variantImageDiv.innerHTML = imageContents;

            if (formattedData.length > 1) {
              containerID.querySelector(".one-color").style.display = "none";
              containerID.querySelector(".more-color").style.display =
                "inline-block";
            } else if (formattedData == 1) {
              containerID.querySelector(".one-color").style.display =
                "inline-block";
              containerID.querySelector(".more-color").style.display = "none";
            } else {
              containerID.querySelector(
                ".product-item-meta__color-count"
              ).style.display = "none";
            }
          } else {
            const count = containerID.querySelector(
              ".product-item-meta__color-count"
            );
            if (count) {
              count.style.display = "none";
            }
          }
        })
        .catch((error) => {
          console.error("Error in fetching storefront products", error);
        });
    }
  }
}
customElements.define("product-item-color", ProductItemColor);

class VariantImage extends HTMLElement {
  constructor() {
    super();

    this.fetchStorefrontProductsCIT();
  }

  fetchStorefrontProductsCIT() {
    const imageVariant = document.getElementById("variant-image-tile");

    const productType = imageVariant.getAttribute("data-product-type");
    const productVendor = imageVariant.getAttribute("data-product-vendor");
    const productID = imageVariant.getAttribute("data-product-id");
    const originalString = imageVariant.getAttribute("data-product-meta");
    const metad = imageVariant.getAttribute("data-meta");
    const lastIndex = originalString.lastIndexOf("-");
    const metafieldID = originalString.substring(0, lastIndex);
    const colorOption = document.getElementById("product-variant-image-option");

    const query = `query {
    products(first: 250, query: "vendor:${productVendor}") {
      edges {
        node {
          availableForSale
          id
          title
          description(truncateAt: 80)
          handle
          tags
          productType
          options {
            name,
            values
          }
          featuredImage {
            src
            height
            width
          }
          images(first: 20) {
            edges {
              node {
                altText
                url
              }
            }
          }
          priceRange {
            maxVariantPrice {
              amount
            }
            minVariantPrice {
              amount
            }
          }
          variants(first: 250) {
            edges {
              node {
                id
                sku
                availableForSale
                compareAtPrice {
                  amount
                }
                price {
                  amount
                }
                title
                selectedOptions {
                  value
                  name
                }
              }
            }
          }
          vendor
          metafield(key: "article_code_supplier", namespace: "specs") {
            value
          }
        }
      }
    }
  }`;

    const requestOptions = {
      method: "POST",
      headers,
      body: query,
    };

    return fetch(
      `https://${Shopify.shop}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(({ data }) => {
        const formattedData = data.products.edges
          .map((productNode) => ({
            objectType: "product",
            available: productNode.node.availableForSale,
            handle: productNode.node.handle,
            url: `/products/${productNode.node.handle}`,
            id: parseInt(
              productNode.node.id.replace("gid://shopify/Product/", "")
            ),
            tags: productNode.node.tags.map((tag) => tag.toLowerCase()),
            productType: productNode.node.productType,
            title: productNode.node.title,
            description: productNode.node.description,
            price: Number(productNode.node.priceRange.minVariantPrice.amount),
            compare_at_price: Number(
              productNode.node.variants.edges[0].node.compareAtPrice?.amount ||
                null
            ),
            featured_image: productNode.node.featuredImage,
            images: productNode.node.images.edges.map(
              (image) => image.node.url
            ),
            metafield: productNode.node.metafield?.value,
            has_only_default_variant:
              productNode.node.options[0].values[0] === "Default Title",
            vendor: productNode.node.vendor,
            ...(productNode.node.options[0] && {
              option1: productNode.node.options[0].name,
            }),
            ...(productNode.node.options[1] && {
              option2: productNode.node.options[1].name,
            }),
            ...(productNode.node.options[2] && {
              option3: productNode.node.options[2].name,
            }),
          }))
          .filter((product) => {
            const lastIndexProd = product.metafield.lastIndexOf("-");
            const metafieldIDProd = product.metafield.substring(
              0,
              lastIndexProd
            );
            return metafieldIDProd.toLowerCase() == metafieldID.toLowerCase();
          });

        if (formattedData.length > 1) {
          const variantImageDiv = document.getElementById(
            "variant-image-picker"
          );
          formattedData.forEach((data) => {
            const imageContents = `
              <a href="${data.url}">

                  <img srcset="${data.featured_image.src}&width=100" sizes="(max-width: 100px) 50px, 100px" loading="eager" class="variant-swatch__image" height="100px" width="100px">

              </a>
      `;
            const tempContainer = document.createElement("div");
            tempContainer.classList.add("variant-swatch");
            tempContainer.classList.add("variant-swatch__item");

            tempContainer.innerHTML = imageContents;

            variantImageDiv.appendChild(tempContainer);
            if (data.id == productID) {
              tempContainer.classList.add("active");
            }
          });
        } else {
          colorOption.style.display = "none";
        }
      })
      .catch((error) => {
        console.error("Error in fetching storefront products", error);
      });
  }
}

customElements.define("variant-image", VariantImage);

// PDP Stock issue fix
document.addEventListener("DOMContentLoaded", function () {
  let currentVariantId = window.location.search.split("variant=")[1];

  function checkInventory(variantId, cartItems) {
    // EQ-FILTERFIX-2026-08-22: buiten een productpagina is er geen variant in
    // beeld; dan werd hier "ProductVariant/undefined" opgevraagd en liep het
    // stuk op een lege node (console-fout op elke collectie-/zoekpagina).
    if (!variantId) return;

    const AddToCartBtn = document.getElementById("AddToCart");

    const query = `{
      nodes(ids: ["gid://shopify/ProductVariant/${variantId}"]) {
          ... on ProductVariant {
          id
          title
          quantityAvailable
        }
      }
    }`;

    const requestOptions = {
      method: "POST",
      headers,
      body: query,
    };

    return fetch(
      `https://${Shopify.shop}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(({ data }) => {
        const quantityAvailable = data && data.nodes && data.nodes[0]
          ? data.nodes[0].quantityAvailable
          : null;
        if (quantityAvailable === null) return;
        if (cartItems) {
          const currentProduct = cartItems.find(
            (x) => x.id === Number(variantId)
          );

          if (currentProduct.quantity >= quantityAvailable) {
            AddToCartBtn.disabled = true;
            AddToCartBtn.innerHTML = `<span>Niet meer op voorraad</span>`;
          } else {
            AddToCartBtn.disabled = false;
            AddToCartBtn.innerHTML = `<span>Toevoegen aan winkelwagen</span>`;
          }
        }
      });
  }

  document.addEventListener("variant:changed", async function (event) {
    currentVariantId = event.detail.variant.id;
    await fetch(Shopify.routes.root + `cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        checkInventory(currentVariantId, cart.items);
      })
      .catch((error) => {
        console.error("Error in fetching storefront products", error);
      });
  });

  document.addEventListener("cart:updated", (event) => {
    checkInventory(currentVariantId, event.detail.cart.items);
  });

  fetch(Shopify.routes.root + `cart.js`)
    .then((response) => response.json())
    .then((cart) => {
      checkInventory(currentVariantId, cart.items);
    })
    .catch((error) => {
      console.error("Error in fetching storefront products", error);
    });
});

// store availability
document.addEventListener("DOMContentLoaded", function () {
  let productCartAttribute = document.querySelector("#has_warehouse");

  // EQ-FILTERFIX-2026-08-22: dit blok is productpagina-code. Op collectie- en
  // zoekpagina's bestaan #has_warehouse en de koopknop niet, en liep het stuk
  // op `null.value` -- drie console-fouten per pageview en de rest van dit
  // blok werd overgeslagen. Buiten een productpagina is er simpelweg niets
  // te doen.
  if (
    !productCartAttribute ||
    !document.querySelector('.product-form .product-form__buy-buttons input[name="id"]')
  ) {
    return;
  }

  fetch(Shopify.routes.root + `cart.js`)
    .then((response) => response.json())
    .then((cart) => {
      if (cart.items.length == 0 && productCartAttribute.value != "true") {
        checkInventory(cart.attributes);
      } else {
        productCartAttribute.value = true;
      }
    })
    .catch((error) => {
      console.error("Error in fetching storefront products", error);
    });

  let currentVariantId = window.location.search.split("variant=")[1];
  const WAREHOUSE_NAME = "EQ Retail";
  const currentId = document.querySelector(
    '.product-form .product-form__buy-buttons input[name="id"]'
  ).value;

  function determineLocation(nodes) {
    let hasWarehouse = false;
    let firstStore = null;
    let warehouseNode = null;

    nodes.forEach((node) => {
      const locationName = node.location.name;
      const isWarehouse = locationName === WAREHOUSE_NAME;

      if (isWarehouse && node.quantityAvailable > 0) {
        hasWarehouse = true;
        warehouseNode = node;
      } else if (!isWarehouse && node.quantityAvailable > 0 && !firstStore) {
        firstStore = node;
      }
    });

    if (hasWarehouse && warehouseNode) {
      console.log(
        `Warehouse available at ${warehouseNode.location.name}. Use warehouse time.`
      );
      hasWarehouse = true;
    } else if (firstStore) {
      console.log(
        `Store available at ${firstStore.location.name}. Use store item.`
      );
      hasWarehouse = false;
    } else {
      console.log("No items available.");
    }
    productCartAttribute.value = hasWarehouse;
  }

  function checkInventory(cartItems) {
    console.log("items", cartItems);
    const AddToCartBtn = document.getElementById("AddToCart");

    const query = `{
      nodes(ids: ["gid://shopify/ProductVariant/${currentId}"]) {
          ... on ProductVariant {
          id
          title
          quantityAvailable
          storeAvailability(first: 250) {
            nodes {
              location {
                name
              }
              quantityAvailable
            }
          }
        }
      }
    }`;

    const requestOptions = {
      method: "POST",
      headers,
      body: query,
    };

    return fetch(
      `https://${Shopify.shop}/api/2023-07/graphql.json`,
      requestOptions
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then(({ data }) => {
        // De Storefront-API geeft null terug als de variant niet (meer) bestaat.
        // Zonder deze controle klapt de winkelvoorraad-logica op de PDP eruit --
        // zelfde soort fout als eerder in checkInventory.
        var node = data && data.nodes && data.nodes[0];
        if (!node || !node.storeAvailability) return;
        determineLocation(node.storeAvailability.nodes);
      });
  }

  document.addEventListener("variant:changed", async function (event) {
    currentVariantId = event.detail.variant.id;
    await fetch(Shopify.routes.root + `cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        checkInventory(cart.items);
      })
      .catch((error) => {
        console.error("Error in fetching storefront products", error);
      });
  });

  document.addEventListener("cart:updated", (event) => {});
});

let filters = document.querySelectorAll('.product-facet__filter-item')
filters.forEach(filter => {
    filter.querySelector('button').setAttribute('aria-expanded', true)
    filter.querySelector('collapsible-content').style.overflow = 'visible'
})
